import { useCallback, useEffect, useRef, useState } from 'react';

import client from '../api/client';
import { connectChatSocket } from '../api/chatSocket';

// Chat conversations across all users (Phase 8 human handoff staff console,
// /api/admin, requireAdmin) — lets staff find bot conversations waiting to
// be picked up, or ones already assigned to a human.
// GET    /admin/chatbot/conversations?mode=&status=&handoffRequested=&page=&limit= -> { items, page, limit, total, pages }.
// DELETE /admin/chatbot/conversations/:id                -> { deleted, id }
const EMPTY = { items: [], page: 1, limit: 20, total: 0, pages: 1 };

export default function useAdminChatConversations({ mode = '', status = '', handoffRequested = false, page = 1, limit = 20 } = {}) {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const filtersRef = useRef({ mode, status });
  const reloadRef = useRef(() => {});

  useEffect(() => {
    filtersRef.current = { mode, status };
  }, [mode, status]);

  const buildParams = useCallback(() => {
    const params = { page, limit };
    if (mode) params.mode = mode;
    if (status) params.status = status;
    if (handoffRequested) params.handoffRequested = true;
    return params;
  }, [mode, status, handoffRequested, page, limit]);

  const reload = useCallback(async () => {
    const { data: res } = await client.get('/admin/chatbot/conversations', { params: buildParams() });
    setData(res && Array.isArray(res.items) ? res : EMPTY);
  }, [buildParams]);

  useEffect(() => {
    reloadRef.current = reload;
  }, [reload]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    client
      .get('/admin/chatbot/conversations', { params: buildParams() })
      .then(({ data: res }) => {
        if (active) setData(res && Array.isArray(res.items) ? res : EMPTY);
      })
      .catch((err) => {
        if (active) {
          setData(EMPTY);
          setError(err);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [buildParams]);

  // Live updates (Phase 8, later addition) — staff auto-join the server's
  // 'admin:chat' room just by connecting (chatSocket.js), so every mode
  // change and requestHumanHandoff flag reaches this list without a manual
  // Refresh. Only ever updates/removes a row already on the current
  // page/filter, in place; a conversation that would newly *appear* under
  // the active filter is left to the 'adminActivity' handler below instead
  // of being spliced in here — inserting it live could put it out of order
  // or ahead of what pagination says is on this page.
  useEffect(() => {
    const socket = connectChatSocket();
    const onUpdate = (updated) => {
      setData((prev) => {
        const exists = prev.items.some((c) => c._id === updated._id);
        if (!exists) return prev;
        const { mode: activeMode, status: activeStatus } = filtersRef.current;
        const stillMatches = (!activeMode || updated.mode === activeMode) && (!activeStatus || updated.status === activeStatus);
        if (!stillMatches) {
          return {
            ...prev,
            items: prev.items.filter((c) => c._id !== updated._id),
            total: Math.max(0, prev.total - 1),
          };
        }
        return { ...prev, items: prev.items.map((c) => (c._id === updated._id ? updated : c)) };
      });
    };
    socket.on('adminConversationUpdate', onUpdate);

    // Any new message, anywhere — including a brand-new conversation's very
    // first one, the one case the merge-in-place handler above can't cover
    // since there's no row yet to update. Unlike that handler this does a
    // real refetch (the only way to get a brand-new row into the right
    // sort/page position), debounced so a burst of messages across several
    // conversations collapses into one request instead of one per message.
    let debounceTimer = null;
    const onActivity = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        Promise.resolve(reloadRef.current()).catch(() => {});
      }, 800);
    };
    socket.on('adminActivity', onActivity);

    return () => {
      clearTimeout(debounceTimer);
      socket.disconnect();
    };
  }, []);

  // Backstop for missed live updates — same reasoning as
  // useAdminChatConversation's identical effect: a backgrounded tab's socket
  // reconnect can silently stall, so refetch the whole list (catching new
  // conversations too, unlike the merge-in-place socket handler above)
  // whenever this tab regains focus.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      Promise.resolve(reloadRef.current()).catch(() => {});
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // takes an id directly (rather than acting on "the" conversation, like
  // useAdminChatConversation's version) so a row can be deleted straight
  // from the list without first selecting/opening it.
  const deleteConversation = useCallback(async (id) => {
    const { data: res } = await client.delete(`/admin/chatbot/conversations/${id}`);
    return res;
  }, []);

  return { ...data, loading, error, reload, deleteConversation };
}
